/**
 * Projects Data
 * Metadata and storytelling content for portfolio projects
 */
import { LucideIcon } from 'lucide-react'
import { Video, Code, Palette, BarChart3, Sparkles, Brain } from 'lucide-react'
export interface Project {
  id: string
  title: string
  subtitle: string
  category: 'video' | 'apps' | 'ux' | 'ai'
  gradient: string
  icon: LucideIcon
  // Visual assets
  thumbnail: string
  heroImage: string
  videoUrl?: string
  // Tags
  tags: string[]
  // Storytelling structure
  story: {
    intentie: {
      title: string
      description: string
    }
    proces: {
      title: string
      description: string
      tools?: string[]
    }
    resultaat: {
      title: string
      description: string
      metrics?: string[]
    }
  }
  // System context
  systemContext: {
    pillar: 'AI Video' | 'VibeCoding' | 'UX Thinking'
    connection: string
  }
}
export const projects: Project[] = [
  {
    id: 'ai-video',
    title: 'AI Video Generation',
    subtitle: 'Character-Driven Storytelling',
    category: 'video',
    gradient: 'from-rose-500 to-pink-600',
    icon: Video,
    thumbnail:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/stCMFT6LkkVykASQVNXRp6/aac4bfad-b894-401d-92ac-ad2a80272b62_%281%29.png',
    heroImage:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/stCMFT6LkkVykASQVNXRp6/aac4bfad-b894-401d-92ac-ad2a80272b62_%281%29.png',
    videoUrl:
      'https://drive.google.com/file/d/14ZHULrGyJQkKQKuUvvja7mO2PQ8pq4y4/preview',
    tags: ['AI', 'Video', 'Storytelling', 'Character Systems'],
    story: {
      intentie: {
        title: 'Van Concept naar Verhaal',
        description:
          'Hoe gebruik je AI om consistente, character-driven video content te maken? Het begon met de vraag: kunnen we gepersonaliseerde verhalen vertellen zonder elke frame handmatig te animeren? Deze karakters - Cyan en Violet - zijn het resultaat van systematische prompt engineering en character consistency workflows.',
      },
      proces: {
        title: 'Character Systems & Prompt Engineering',
        description:
          'Ik bouwde een character system: eerst definieer je wie je personages zijn (uiterlijk, persoonlijkheid, kleding), dan gebruik je dat als template voor alle scenes. Cyan met haar vibrante blauwe haar en Violet met haar mysterieuze paarse look werden consistent gegenereerd door gestructureerde prompts. Elke scene behoudt character consistency door het systeem.',
        tools: [
          'Midjourney',
          'Runway ML',
          'Custom Prompts',
          'Character Templates',
        ],
      },
      resultaat: {
        title: 'Schaalbare Video Productie',
        description:
          'Het resultaat: een workflow waarmee je in uren video content maakt die normaal dagen kost. De character consistency is hoog, en het verhaal blijft coherent omdat het systeem de rode draad bewaart. Cyan en Violet kunnen nu in oneindig veel scenarios worden gebruikt.',
        metrics: [
          '3 unique characters',
          '10+ scenes generated',
          '90% consistency',
        ],
      },
    },
    systemContext: {
      pillar: 'AI Video',
      connection:
        'Dit project demonstreert mijn AI Video Generation pillar: creative storytelling met systematische AI-integratie en character consistency.',
    },
  },
  {
    id: 'bulkr',
    title: 'BULKR App Design',
    subtitle: 'AI-Driven Shopping Experience',
    category: 'apps',
    gradient: 'from-emerald-500 to-teal-600',
    icon: Code,
    thumbnail:
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop',
    heroImage:
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&h=800&fit=crop',
    tags: ['React', 'AI', 'UX', 'E-commerce'],
    story: {
      intentie: {
        title: 'Smart Shopping, Simpel Gemaakt',
        description:
          'De vraag: hoe maak je bulk-shopping intuïtief? Gebruikers willen snel grote hoeveelheden bestellen, maar bestaande apps zijn clunky. AI kan helpen met aanbevelingen en voorspellingen.',
      },
      proces: {
        title: 'UX Research → Rapid Prototyping',
        description:
          'Eerst user research: wat frustreert gebruikers? Dan schetsen, wireframes, en snel prototypen met React. AI-integratie voor slimme suggesties op basis van koopgedrag. Iteratief testen en verfijnen.',
        tools: ['Figma', 'React', 'TypeScript', 'AI API'],
      },
      resultaat: {
        title: 'Intuïtieve Shopping Flow',
        description:
          'Een app die voelt als magie: je begint met typen, AI suggereert producten, en binnen 3 taps ben je klaar. Snelheid en simpliciteit als kernwaarden.',
        metrics: ['3-tap checkout', '40% sneller', 'AI suggesties'],
      },
    },
    systemContext: {
      pillar: 'VibeCoding',
      connection:
        'Dit project toont mijn VibeCoding aanpak: van UX thinking naar werkende app in dagen, niet maanden.',
    },
  },
  {
    id: 'dashboard',
    title: 'Interactive Dashboard',
    subtitle: 'Real-Time Data Visualization',
    category: 'apps',
    gradient: 'from-blue-500 to-indigo-600',
    icon: BarChart3,
    thumbnail:
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop',
    heroImage:
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop',
    tags: ['Dashboard', 'Data Viz', 'React', 'Real-time'],
    story: {
      intentie: {
        title: 'Data Begrijpbaar Maken',
        description:
          'Organisaties verdrinken in data maar missen inzicht. De uitdaging: hoe visualiseer je complexe data zo dat iedereen het snapt, zonder overload?',
      },
      proces: {
        title: 'Component-Based Architecture',
        description:
          'Ik bouwde een modulair dashboard systeem: elke widget is een herbruikbaar component. Real-time data via WebSockets, smooth animaties voor state changes, en responsive layout voor alle schermen.',
        tools: ['React', 'D3.js', 'WebSocket', 'Tailwind'],
      },
      resultaat: {
        title: 'Actionable Insights Dashboard',
        description:
          'Een dashboard dat niet alleen data toont, maar verhalen vertelt. Gebruikers zien direct wat belangrijk is en kunnen snel actie ondernemen.',
        metrics: ['Real-time updates', '12+ widgets', 'Mobile responsive'],
      },
    },
    systemContext: {
      pillar: 'VibeCoding',
      connection:
        'Rapid prototyping van een complex systeem, met focus op gebruikerservaring en performance.',
    },
  },
  {
    id: 'camping-ux',
    title: 'Camping App UX',
    subtitle: 'Group Experience Design',
    category: 'ux',
    gradient: 'from-purple-500 to-violet-600',
    icon: Palette,
    thumbnail:
      'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800&h=600&fit=crop',
    heroImage:
      'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=1200&h=800&fit=crop',
    tags: ['UX', 'Mobile', 'Social', 'Design Thinking'],
    story: {
      intentie: {
        title: 'Groepsbeleving Centraal',
        description:
          'Camping met vrienden is leuk, maar de planning is chaos. Hoe maak je een app die groepen helpt plannen zonder frustratie?',
      },
      proces: {
        title: 'Empathisch Ontwerpen',
        description:
          'User interviews met campinggroepen, persona mapping, en journey mapping. Focus op de emotionele ervaring: hoe voelt het om samen te plannen? Prototypes testen met echte groepen.',
        tools: ['Figma', 'User Testing', 'Journey Mapping', 'Prototyping'],
      },
      resultaat: {
        title: 'Samen Plannen, Samen Genieten',
        description:
          'Een app die het plannen zo simpel maakt dat de focus blijft op de beleving. Gedeelde lijsten, stemmen op activiteiten, en real-time updates voor iedereen.',
        metrics: ['5 min setup', 'Group voting', 'Real-time sync'],
      },
    },
    systemContext: {
      pillar: 'UX Thinking',
      connection:
        'Empathisch ontwerpen voor groepservaringen, met focus op menselijke interactie boven features.',
    },
  },
  {
    id: 'motion-design',
    title: 'Motion Design System',
    subtitle: 'Micro-Interactions That Matter',
    category: 'video',
    gradient: 'from-orange-500 to-amber-600',
    icon: Sparkles,
    thumbnail:
      'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800&h=600&fit=crop',
    heroImage:
      'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=1200&h=800&fit=crop',
    tags: ['Animation', 'Motion', 'UI', 'Design System'],
    story: {
      intentie: {
        title: 'Beweging Met Betekenis',
        description:
          'Animaties zijn niet decoratie — ze zijn communicatie. Hoe bouw je een motion system dat consistent is en betekenis toevoegt?',
      },
      proces: {
        title: 'Systematisch Animeren',
        description:
          'Ik definieerde motion principles: timing, easing, en purpose. Elke animatie heeft een reden: feedback, richting, of emotie. Gebouwd als herbruikbaar systeem met tokens en presets.',
        tools: ['Framer Motion', 'After Effects', 'React', 'Design Tokens'],
      },
      resultaat: {
        title: 'Levende Interfaces',
        description:
          'Een motion library die interfaces tot leven brengt zonder afleidend te zijn. Van button states tot page transitions — alles voelt natuurlijk en intentioneel.',
        metrics: ['20+ animations', 'Reusable system', '60fps performance'],
      },
    },
    systemContext: {
      pillar: 'UX Thinking',
      connection:
        'Motion als onderdeel van UX: het versterkt de gebruikerservaring door feedback en richting te geven.',
    },
  },
  {
    id: 'prompt-engineering',
    title: 'AI Prompt Engineering',
    subtitle: 'Systematic Creative Generation',
    category: 'ai',
    gradient: 'from-violet-500 to-purple-600',
    icon: Brain,
    thumbnail:
      'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop',
    heroImage:
      'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=800&fit=crop',
    tags: ['AI', 'Prompts', 'Systems', 'Creative'],
    story: {
      intentie: {
        title: 'Van Chaos naar Systeem',
        description:
          'AI is krachtig, maar inconsistent. Hoe bouw je een prompt system dat betrouwbare, consistente output genereert?',
      },
      proces: {
        title: 'Template-Based Prompting',
        description:
          'Ik ontwikkelde een prompt template systeem: baseer prompts op herbruikbare structuren, test variaties, en documenteer wat werkt. Iteratief verfijnen tot je een library hebt van proven prompts.',
        tools: ['GPT-4', 'Midjourney', 'Custom Templates', 'Testing Framework'],
      },
      resultaat: {
        title: 'Betrouwbare AI Output',
        description:
          'Een prompt library die consistent kwalitatieve output levert. Van character design tot scene generation — elk prompt is getest en gedocumenteerd.',
        metrics: ['50+ templates', '90% consistency', 'Documented system'],
      },
    },
    systemContext: {
      pillar: 'AI Video',
      connection:
        'Systematisch prompt engineering is de basis van mijn AI Video Generation workflow.',
    },
  },
]
